import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type {
  Store,
  UserLocation,
  LocationPermission,
  DeliveryAddress,
  SavedAddress,
  Screen,
  DeliveryEligibility,
  NonEligibleStore,
} from '@/types'
import type {
  AppConfig,
  FeatureFlags,
  ThemeConfig,
  RestaurantConfig,
  LocaleConfig,
  ApiConfig,
} from '@/types/config'
import { defaultFeatures, defaultLocale, defaultApi } from '@/types/config'
import { isWithinRadius } from '@/utils/distance'

// ============================================================================
// HELPER: Get AppConfig from window or use defaults
// ============================================================================

function getAppConfig(): AppConfig | null {
  if (typeof window !== 'undefined' && window.AppConfig) {
    return window.AppConfig
  }
  return null
}

// ============================================================================
// AUTH TYPES
// ============================================================================

export interface AuthUser {
  id: string
  email?: string
  phone?: string
  displayName?: string
  photoUrl?: string
  provider: 'google' | 'apple' | 'phone'
}

// ============================================================================
// FILTER/SORT TYPES
// ============================================================================

export type SortOption = 'distance' | 'fastest' | 'rating' | 'name'

export interface FilterOptions {
  openNow: boolean
  hasDelivery: boolean
  hasFreeDelivery: boolean
}

// ============================================================================
// STATE INTERFACE
// ============================================================================

interface StoreSelectionState {
  // Core state
  userLocation: UserLocation | null
  locationPermission: LocationPermission
  deliveryAddress: DeliveryAddress | null
  addressSearchQuery: string
  savedAddresses: SavedAddress[]
  recentAddresses: DeliveryAddress[]
  stores: Store[]
  storesLoading: boolean
  storesError: string | null
  selectedStoreId: string | null
  currentScreen: Screen
  isSearching: boolean
  
  // Auth
  isAuthenticated: boolean
  user: AuthUser | null
  authLoading: boolean
  
  // Search & Filter
  searchQuery: string
  sortBy: SortOption
  filters: FilterOptions
  
  // Config (from window.AppConfig)
  restaurant: RestaurantConfig
  theme: ThemeConfig
  features: FeatureFlags
  locale: LocaleConfig
  api: ApiConfig
  configLoaded: boolean
}

// ============================================================================
// ACTIONS
// ============================================================================

type Action =
  | { type: 'SET_USER_LOCATION'; payload: UserLocation | null }
  | { type: 'SET_LOCATION_PERMISSION'; payload: LocationPermission }
  | { type: 'SET_DELIVERY_ADDRESS'; payload: DeliveryAddress | null }
  | { type: 'SET_ADDRESS_SEARCH_QUERY'; payload: string }
  | { type: 'SET_STORES'; payload: Store[] }
  | { type: 'SET_STORES_LOADING'; payload: boolean }
  | { type: 'SET_STORES_ERROR'; payload: string | null }
  | { type: 'SELECT_STORE'; payload: string | null }
  | { type: 'SET_CURRENT_SCREEN'; payload: Screen }
  | { type: 'SET_IS_SEARCHING'; payload: boolean }
  | { type: 'SET_AUTH_USER'; payload: AuthUser | null }
  | { type: 'SET_AUTH_LOADING'; payload: boolean }
  | { type: 'TOGGLE_FAVORITE'; payload: string }
  | { type: 'SET_FAVORITES'; payload: Set<string> }
  | { type: 'SET_SEARCH_QUERY'; payload: string }
  | { type: 'SET_SORT_BY'; payload: SortOption }
  | { type: 'SET_FILTERS'; payload: Partial<FilterOptions> }
  | { type: 'LOAD_CONFIG'; payload: Partial<AppConfig> }
  | { type: 'RESET_STATE' }

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultRestaurant: RestaurantConfig = {
  id: '',
  name: 'Restaurant Name',
  tagline: 'Your tagline here',
  logoUrl: '',
  faviconUrl: '/vite.svg',
  contact: {
    email: '',
    phone: '',
    website: '',
  },
}

const defaultTheme: ThemeConfig = {
  brandPrimary: '#B91C1C',
  brandPrimaryHover: '#991B1B',
  brandPrimaryLight: '#FEE2E2',
  brandSecondary: '#78350F',
  surfacePrimary: '#FFFFFF',
  surfaceSecondary: '#F9FAFB',
  surfaceTertiary: '#F3F4F6',
  surfaceElevated: '#FFFFFF',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textTertiary: '#9CA3AF',
  textInverse: '#FFFFFF',
  borderLight: '#E5E7EB',
  borderDefault: '#D1D5DB',
  borderStrong: '#9CA3AF',
  statusSuccess: '#10B981',
  statusSuccessLight: '#D1FAE5',
  statusWarning: '#F59E0B',
  statusWarningLight: '#FEF3C7',
  statusError: '#EF4444',
  statusErrorLight: '#FEE2E2',
  statusInfo: '#3B82F6',
  statusInfoLight: '#DBEAFE',
}

// ============================================================================
// INITIAL STATE
// ============================================================================

function getInitialState(): StoreSelectionState {
  const config = getAppConfig()
  
  return {
    userLocation: null,
    locationPermission: 'prompt',
    deliveryAddress: null,
    addressSearchQuery: '',
    savedAddresses: config?.savedAddresses || [],
    recentAddresses: config?.recentAddresses || [],
    stores: config?.stores || [],
    storesLoading: !config?.stores?.length,
    storesError: null,
    selectedStoreId: null,
    currentScreen: 'main',
    isSearching: false,
    
    // Auth - from injected config or default
    isAuthenticated: config?.auth?.isAuthenticated || false,
    user: config?.auth?.user ? {
      ...config.auth.user,
      provider: 'google', // Default provider
    } : null,
    authLoading: false,
    
    // Search & Filter
    searchQuery: '',
    sortBy: 'distance',
    filters: {
      openNow: false,
      hasDelivery: false,
      hasFreeDelivery: false,
    },
    
    // Config from window.AppConfig
    restaurant: config?.restaurant || defaultRestaurant,
    theme: config?.theme || defaultTheme,
    features: config?.features || defaultFeatures,
    locale: config?.locale || defaultLocale,
    api: config?.api || defaultApi,
    configLoaded: !!config,
  }
}

// ============================================================================
// REDUCER
// ============================================================================

function storeSelectionReducer(
  state: StoreSelectionState,
  action: Action
): StoreSelectionState {
  switch (action.type) {
    case 'SET_USER_LOCATION':
      return { ...state, userLocation: action.payload }
    case 'SET_LOCATION_PERMISSION':
      return { ...state, locationPermission: action.payload }
    case 'SET_DELIVERY_ADDRESS':
      return { ...state, deliveryAddress: action.payload }
    case 'SET_ADDRESS_SEARCH_QUERY':
      return { ...state, addressSearchQuery: action.payload }
    case 'SET_STORES':
      return { ...state, stores: action.payload }
    case 'SET_STORES_LOADING':
      return { ...state, storesLoading: action.payload }
    case 'SET_STORES_ERROR':
      return { ...state, storesError: action.payload }
    case 'SELECT_STORE':
      return { ...state, selectedStoreId: action.payload }
    case 'SET_CURRENT_SCREEN':
      return { ...state, currentScreen: action.payload }
    case 'SET_IS_SEARCHING':
      return { ...state, isSearching: action.payload }
    case 'SET_AUTH_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: action.payload !== null,
      }
    case 'SET_AUTH_LOADING':
      return { ...state, authLoading: action.payload }
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload }
    case 'SET_SORT_BY':
      return { ...state, sortBy: action.payload }
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } }
    case 'LOAD_CONFIG': {
      const config = action.payload
      return {
        ...state,
        restaurant: config.restaurant || state.restaurant,
        theme: config.theme || state.theme,
        features: config.features || state.features,
        locale: config.locale || state.locale,
        api: config.api || state.api,
        stores: config.stores || state.stores,
        savedAddresses: config.savedAddresses || state.savedAddresses,
        recentAddresses: config.recentAddresses || state.recentAddresses,
        isAuthenticated: config.auth?.isAuthenticated || state.isAuthenticated,
        user: config.auth?.user ? { ...config.auth.user, provider: 'google' } : state.user,
        configLoaded: true,
        storesLoading: false,
      }
    }
    case 'RESET_STATE':
      return getInitialState()
    default:
      return state
  }
}

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface StoreSelectionContextValue {
  state: StoreSelectionState
  dispatch: React.Dispatch<Action>
  
  // Computed values
  sortedPickupStores: Store[]
  filteredStores: Store[]
  eligibleDeliveryStores: Store[]
  deliveryEligibility: Map<string, DeliveryEligibility>
  nonEligibleStores: NonEligibleStore[]
  
  // Helper actions
  setDeliveryAddress: (address: DeliveryAddress | null) => void
  selectStore: (storeId: string) => void
  navigateTo: (screen: Screen) => void
  goBack: () => void
  
  // Auth actions
  loginWithGoogle: () => Promise<void>
  loginWithApple: () => Promise<void>
  loginWithPhone: () => Promise<void>
  logout: () => void
  
  // Search & Filter
  setSearchQuery: (query: string) => void
  setSortBy: (sort: SortOption) => void
  setFilter: (filter: Partial<FilterOptions>) => void
  clearFilters: () => void
  
  // Store actions
  refreshStores: () => Promise<void>
}

// ============================================================================
// CONTEXT
// ============================================================================

const StoreSelectionContext = createContext<StoreSelectionContextValue | null>(null)

// ============================================================================
// PROVIDER
// ============================================================================

interface StoreSelectionProviderProps {
  children: ReactNode
}

export function StoreSelectionProvider({ children }: StoreSelectionProviderProps) {
  const [state, dispatch] = useReducer(storeSelectionReducer, undefined, getInitialState)

  // Apply CSS custom properties from theme
  useEffect(() => {
    const root = document.documentElement
    const theme = state.theme
    
    // Apply light mode colors
    root.style.setProperty('--color-brand-primary', theme.brandPrimary)
    root.style.setProperty('--color-brand-primary-hover', theme.brandPrimaryHover)
    root.style.setProperty('--color-brand-primary-light', theme.brandPrimaryLight)
    root.style.setProperty('--color-brand-secondary', theme.brandSecondary)
    root.style.setProperty('--color-surface-primary', theme.surfacePrimary)
    root.style.setProperty('--color-surface-secondary', theme.surfaceSecondary)
    root.style.setProperty('--color-surface-tertiary', theme.surfaceTertiary)
    root.style.setProperty('--color-surface-elevated', theme.surfaceElevated)
    root.style.setProperty('--color-text-primary', theme.textPrimary)
    root.style.setProperty('--color-text-secondary', theme.textSecondary)
    root.style.setProperty('--color-text-tertiary', theme.textTertiary)
    root.style.setProperty('--color-text-inverse', theme.textInverse)
    root.style.setProperty('--color-border-light', theme.borderLight)
    root.style.setProperty('--color-border-default', theme.borderDefault)
    root.style.setProperty('--color-border-strong', theme.borderStrong)
    root.style.setProperty('--color-status-success', theme.statusSuccess)
    root.style.setProperty('--color-status-success-light', theme.statusSuccessLight)
    root.style.setProperty('--color-status-warning', theme.statusWarning)
    root.style.setProperty('--color-status-warning-light', theme.statusWarningLight)
    root.style.setProperty('--color-status-error', theme.statusError)
    root.style.setProperty('--color-status-error-light', theme.statusErrorLight)
    root.style.setProperty('--color-status-info', theme.statusInfo)
    root.style.setProperty('--color-status-info-light', theme.statusInfoLight)
    
    // Update page title and favicon
    document.title = state.restaurant.name 
      ? `Select Your Store | ${state.restaurant.name}` 
      : 'Select Your Store'
    
    const favicon = document.querySelector('link[rel="icon"]')
    if (favicon && state.restaurant.faviconUrl) {
      favicon.setAttribute('href', state.restaurant.faviconUrl)
    }
    
    // Update theme-color meta tag
    const themeColor = document.querySelector('meta[name="theme-color"]')
    if (themeColor) {
      themeColor.setAttribute('content', theme.brandPrimary)
    }
  }, [state.theme, state.restaurant])

  // Mark loading as complete when config is loaded
  useEffect(() => {
    if (state.configLoaded && state.stores.length > 0) {
      dispatch({ type: 'SET_STORES_LOADING', payload: false })
    } else if (state.configLoaded && state.stores.length === 0) {
      dispatch({ type: 'SET_STORES_ERROR', payload: 'No stores configured' })
      dispatch({ type: 'SET_STORES_LOADING', payload: false })
    }
  }, [state.configLoaded, state.stores.length])

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Filter and sort stores
  const filteredStores = useMemo(() => {
    let stores = [...state.stores]

    // Apply search filter
    if (state.searchQuery.trim()) {
      const query = state.searchQuery.toLowerCase()
      stores = stores.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          (s.address?.toLowerCase().includes(query)) ||
          (s.area?.toLowerCase().includes(query))
      )
    }

    // Apply filters
    if (state.filters.openNow) {
      stores = stores.filter((s) => s.status === 'open' || s.status === 'closing_soon')
    }
    if (state.filters.hasDelivery) {
      stores = stores.filter((s) => s.delivery?.available || s.isDeliveryOn)
    }
    if (state.filters.hasFreeDelivery) {
      stores = stores.filter((s) => s.delivery?.available && s.delivery.fee.min === 0)
    }

    // Apply sorting
    switch (state.sortBy) {
      case 'distance':
        stores.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
        break
      case 'fastest':
        stores.sort((a, b) => (a.pickup?.estimatedTime.min ?? Infinity) - (b.pickup?.estimatedTime.min ?? Infinity))
        break
      case 'rating':
        stores.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))
        break
      case 'name':
        stores.sort((a, b) => a.name.localeCompare(b.name))
        break
    }

    return stores
  }, [state.stores, state.searchQuery, state.filters, state.sortBy])

  // Sort stores by distance for pickup
  const sortedPickupStores = useMemo(() => {
    const stores = [...filteredStores].filter((s) => s.isPickupOn !== false)
    if (state.userLocation) {
      return stores.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
    }
    return stores.sort((a, b) => a.name.localeCompare(b.name))
  }, [filteredStores, state.userLocation])

  // Calculate delivery eligibility
  const { eligibleDeliveryStores, deliveryEligibility, nonEligibleStores } = useMemo(() => {
    if (!state.deliveryAddress) {
      return {
        eligibleDeliveryStores: [],
        deliveryEligibility: new Map<string, DeliveryEligibility>(),
        nonEligibleStores: [],
      }
    }

    const eligibility = new Map<string, DeliveryEligibility>()
    const nonEligible: NonEligibleStore[] = []
    const eligible: Store[] = []

    // Filter for stores with delivery enabled
    const deliveryStores = filteredStores.filter((s) => s.isDeliveryOn !== false)

    deliveryStores.forEach((store) => {
      if (!store.delivery?.available && !store.isDeliveryOn) {
        nonEligible.push({ storeId: store.storeId, reason: 'delivery_unavailable' })
        return
      }

      if (store.status === 'closed') {
        nonEligible.push({ storeId: store.storeId, reason: 'store_closed' })
        return
      }

      const inRange = isWithinRadius(
        store.addressLocation?.lat ?? 0,
        store.addressLocation?.long ?? 0,
        state.deliveryAddress!.latitude,
        state.deliveryAddress!.longitude,
        store.delivery?.radius || 5000
      )

      if (!inRange) {
        nonEligible.push({ storeId: store.storeId, reason: 'out_of_range' })
        return
      }

      eligible.push(store)
      eligibility.set(store.storeId, {
        storeId: store.storeId,
        deliveryFee: store.delivery?.fee.min ?? 0,
        estimatedTime: store.delivery?.estimatedTime ?? { min: 0, max: 0 },
        distance: store.distance ?? 0,
        minOrderValue: store.delivery?.minOrderValue,
      })
    })

    // Sort by estimated delivery time
    eligible.sort((a, b) => (a.delivery?.estimatedTime.min ?? Infinity) - (b.delivery?.estimatedTime.min ?? Infinity))

    return {
      eligibleDeliveryStores: eligible,
      deliveryEligibility: eligibility,
      nonEligibleStores: nonEligible,
    }
  }, [filteredStores, state.deliveryAddress])

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const setDeliveryAddress = useCallback((address: DeliveryAddress | null) => {
    dispatch({ type: 'SET_DELIVERY_ADDRESS', payload: address })
  }, [])

  const selectStore = useCallback((storeId: string) => {
    dispatch({ type: 'SELECT_STORE', payload: storeId })
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(15)
    }
  }, [])

  const navigateTo = useCallback((screen: Screen) => {
    dispatch({ type: 'SET_CURRENT_SCREEN', payload: screen })
  }, [])

  const goBack = useCallback(() => {
    const screenHistory: Record<Screen, Screen> = {
      main: 'main',
      address_search: 'main',
      delivery_results: 'main',
      map_view: 'main',
    }
    dispatch({ type: 'SET_CURRENT_SCREEN', payload: screenHistory[state.currentScreen] })
  }, [state.currentScreen])

  // Auth actions - placeholder implementations
  const loginWithGoogle = useCallback(async () => {
    dispatch({ type: 'SET_AUTH_LOADING', payload: true })
    // TODO: Implement actual Firebase Google auth
    await new Promise((resolve) => setTimeout(resolve, 1000))
    const mockUser: AuthUser = {
      id: 'user-google-123',
      email: 'user@gmail.com',
      displayName: 'John Doe',
      provider: 'google',
    }
    dispatch({ type: 'SET_AUTH_USER', payload: mockUser })
    dispatch({ type: 'SET_AUTH_LOADING', payload: false })
  }, [])

  const loginWithApple = useCallback(async () => {
    dispatch({ type: 'SET_AUTH_LOADING', payload: true })
    // TODO: Implement actual Firebase Apple auth
    await new Promise((resolve) => setTimeout(resolve, 1000))
    const mockUser: AuthUser = {
      id: 'user-apple-456',
      email: 'user@icloud.com',
      displayName: 'John D.',
      provider: 'apple',
    }
    dispatch({ type: 'SET_AUTH_USER', payload: mockUser })
    dispatch({ type: 'SET_AUTH_LOADING', payload: false })
  }, [])

  const loginWithPhone = useCallback(async () => {
    dispatch({ type: 'SET_AUTH_LOADING', payload: true })
    // TODO: Implement actual Firebase Phone auth
    await new Promise((resolve) => setTimeout(resolve, 1000))
    const mockUser: AuthUser = {
      id: 'user-phone-789',
      phone: '+61412345678',
      displayName: 'Mobile User',
      provider: 'phone',
    }
    dispatch({ type: 'SET_AUTH_USER', payload: mockUser })
    dispatch({ type: 'SET_AUTH_LOADING', payload: false })
  }, [])

  const logout = useCallback(() => {
    dispatch({ type: 'SET_AUTH_USER', payload: null })
  }, [])

  // Search & Filter
  const setSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query })
  }, [])

  const setSortBy = useCallback((sort: SortOption) => {
    dispatch({ type: 'SET_SORT_BY', payload: sort })
  }, [])

  const setFilter = useCallback((filter: Partial<FilterOptions>) => {
    dispatch({ type: 'SET_FILTERS', payload: filter })
  }, [])

  const clearFilters = useCallback(() => {
    dispatch({
      type: 'SET_FILTERS',
      payload: { openNow: false, hasDelivery: false, hasFreeDelivery: false },
    })
    dispatch({ type: 'SET_SEARCH_QUERY', payload: '' })
  }, [])

  // Refresh stores (re-read from AppConfig)
  const refreshStores = useCallback(async () => {
    const config = getAppConfig()
    if (config) {
      dispatch({ type: 'LOAD_CONFIG', payload: config })
    }
  }, [])

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: StoreSelectionContextValue = useMemo(
    () => ({
      state,
      dispatch,
      sortedPickupStores,
      filteredStores,
      eligibleDeliveryStores,
      deliveryEligibility,
      nonEligibleStores,
      setDeliveryAddress,
      selectStore,
      navigateTo,
      goBack,
      loginWithGoogle,
      loginWithApple,
      loginWithPhone,
      logout,
      setSearchQuery,
      setSortBy,
      setFilter,
      clearFilters,
      refreshStores,
    }),
    [
      state,
      sortedPickupStores,
      filteredStores,
      eligibleDeliveryStores,
      deliveryEligibility,
      nonEligibleStores,
      setDeliveryAddress,
      selectStore,
      navigateTo,
      goBack,
      loginWithGoogle,
      loginWithApple,
      loginWithPhone,
      logout,
      setSearchQuery,
      setSortBy,
      setFilter,
      clearFilters,
      refreshStores,
    ]
  )

  return (
    <StoreSelectionContext.Provider value={value}>
      {children}
    </StoreSelectionContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

export function useStoreSelection() {
  const context = useContext(StoreSelectionContext)
  if (!context) {
    throw new Error('useStoreSelection must be used within a StoreSelectionProvider')
  }
  return context
}
